export namespace AssemblyConfig {
	export type Component = Tach | Shaft | Gear | Bearing | Tracker | Flutter | Engine | Sensor;
	export enum HumsHiThreshold {
		Alarm = 0.99,
		Warning = 0.75,
		PreWarning = 0.5,
	}
	export enum RTBThreshold {
		Warning = 0.4,
	}
	export interface CompleteConfig {
		hash: string;
		hashretrieved: number;
		asmtype: {
			name: string;
			asset: string;
			engine: string;
			gearbox: string;
		};
		display: {
			exceedances: {
				exceedance: ExceedanceDef[];
			};
			lrus: {
				lru: LRUDef[];
			};
			tach: Tach[];
			tracker: Tracker[];
			shaft: Shaft[];
			flutter: Flutter[];
			engine: Engine[];
			sensor: Sensor[];
			item?: any[];
			regimes?: APIResults.RegimeIndex;
			parameters: StateParameter[];
			rtb?: {
				rotor: APIResults.Rotor[];
				vcheck: APIResults.VibeCheck;
			};
		};
		findParentShaft: (component_code: string) => Shaft;
		findComponent: (component_code: string) => Component;
		findTach: (device_id: number) => Tach;
		getAllComponents: () => Component[];
		getParameter: (paramIndex: number) => StateParameter;

		/** The number of times an alert can be cleared */
		alert_clear_limit: number;
		/** The number of hours time window applied to the above clear limit */
		alert_clear_period: number;
		/** The nominal data collection period for this aircraft */
		nominal_dt: number;
		/** The remaining useful life plant noise, or the maximum the rul can change in an hour */
		var_rs: number;
		/** The remaining useful life measurement noise */
		var_rr: number;
		/** The calculation of confidence. An drul = -1 and d2rul = 0, this is a perfect prognostic
		 * A drul_hconf_lmt 2 and d2rul_hconf_lmt of .5 give a range of dRUL of 1 to -3 and a d2rul of +/- 0.5 */
		drul_hconf_lmt: number;
		drul_mconf_lmt: number;
		d2rul_hconf_lmt: number;
		d2rul_mconf_lmt: number;
		/** The Capacity Factor for RUL is the amount of time the turbine is actually running
		 * to extend the time estimate for rul trend calculation */
		rul_capacity_factor: number;
		/** The number of acquisitions to look back in time for the trend and rul calculation */
		look_back_acq: number;
		/** The minimum number of acquisitions required to performa a meaningful RUL calculation */
		min_rul_acq: number;
		/** The number of hours to look forward in time for the trend prediction calculation */
		look_ahead_pred: number;
		/** When importing ACSTATE files, one in every N "st" rows will be imported */
		acstate_import_interval_sec: number;
		/** Name for alternate engine configuration */
		alt_engine_name: string;
		/** Icon file to use for asset */
		alt_asset_icon: string;
		/** Alternate term for asset ie "Vehicle" */
		alt_asset_term: string;
		/** Number of the alternate HI calc algorithm to use if any */
		alt_hi_trend: number;
		/** Threshold to generate an RUL alert below */
		prog_alert_hrs: number;
		/** Number of hours to show OBCUSTAT icon in alarm (default 24) */
		fixed_obcustat_red_hours: number;
		/** If set to "true", show the "counts over time" section in the FDM tab. Defaults to false. */
		enable_counts_gui: boolean;
	}
	/** Shared by all components */
	export interface ComponentBase {
		/** Descriptive name */
		name: string;
		/** Component code/key */
		id: string;
		/** Critical CIs used to calculate HI */
		ci: AssemblyConfig.CI[];
		/** Optional CIs for this component that are not used in HI calcs */
		optional_ci: any;
		type: string;
		state?: ComponentState;
		var_s: number;
		var_r: number;
		mtbf: number;
		use_rul: boolean;
		scale: number[];
		offset?: string;
		mean?: string;
		warn?: string;
		alarm?: string;
		lru: number;
		limit?: number;
	}
	export interface ComponentState {
		timestamp: Date;
		hi: number;
		filtered_hi: number;
		rul: number;
	}

	export interface Tach extends ComponentBase {
		type: "tach";
		devid: number;
		ppr: number;
		ratio: number;
		rll: number;
	}

	export interface Shaft extends ComponentBase {
		type: "shaft";
		altid: string;
		ratio: number;
		gear?: Gear[];
		bearing?: Bearing[];
	}

	export interface Gear extends ComponentBase {
		type: "gear";
		teeth: number;
		bw: number;
		brnid: string;
	}

	export interface Bearing extends ComponentBase {
		type: "bearing";
		race: Race[];
		window: Window;
		icon?: string;
	}

	export interface Race {
		name: string;
		value: number;
	}

	export interface Tracker extends ComponentBase {
		type: "tracker";
		rotor: string;
	}

	export interface Window {
		id: string;
		low: number;
		hi: number;
		psdlength: number;
		overlap: number;
	}

	export interface Sensor extends ComponentBase {
		type: "sensor";
	}

	export interface Flutter extends ComponentBase {
		type: "flutter";
	}

	export interface Engine extends ComponentBase {
		type: "engine";
		engtype: string;
		engtypealt?: string;
	}

	export interface BaseCI {
		name: string;
		index: number;
		units: string;
		detail: string;
		plot?: number;
		groupname?: string;
	}

	export interface CI extends BaseCI {
		warning: number;
		alarm: number;
		offset: number;
		mean: number;
	}

	export interface ExceedanceDef {
		id: number;
		name: string;
		desc: string;
		severity?: number;
	}

	export interface LRUDef {
		id: number;
		name: string;
	}

	export interface StateParameter {
		id: number;
		name: string;
		units: string;
		scale: number;
		order: number;
		default: boolean;
		selected?: boolean;
	}

	export interface Cam {
		id: number;
		type: string;
		desc: string;
		channel: number;
	}
}

export namespace APIResults {
	export interface Fleet {
		fleet_id: number;
		organization_id?: number;
		organization_name?: string;
		fleet_name: string;
		time_offset_hours: number | null;
		hidden: boolean;
		assets?: FleetAsset[];
		totalhimax: number;
		mdmax: number;
		exceedancecount: number;
		latest_obcu_failures: number;
		rtbhealth: number;
		rulmin?: number;
		flight_time?: number;
	}

	export interface Asset {
		asset_id: number;
		fleet_id: number;
		fleet_name?: string;
		time_offset_hours?: number;
		fleet_hidden: boolean;
		organization_id: number;
		organization_name?: string;
		asset_type_id: number;
		asset_type_name?: string;
		asset_name: string;
		asset_serial_number?: string;
		obcu_serial_number: string;
		enabled: boolean;
		hidden: boolean;
		hide_map: boolean;
		is_fixed: boolean;
		last_login: Date;
		last_update: Date;
		gonedark_days: number;
		gonedark_suspend: boolean;
		alt_engine: boolean;
		override_heading: boolean;
		flight_time_offset: number;
		sim_iccid: string;
		hw_serial_number: string;
		mac_address: string;
		board_revision: string;
		severe_exceedances_only: boolean;
	}

	export interface FleetAsset extends Asset {
		mdmax: number;
		mdmax_component: string;
		mdmax_component_name: string | null;
		exceedancecount: number;
		severeexceedancecount: number;
		alertcount: number;
		totalhimax: number;
		rulmin: number;
		rulmin_component: string;
		rulmin_component_name: string | null;
		rtbmax: number;
		rtbhealth: number;
		defaultvshaft?: string;
		flight_time: number;
		latest_assembly_version: string;
		latest_obcu_failures: number;
		alt_asset_term: string;
		last_operation_date?: Date;
		mdstatus: string;
		fdmstatus: string;
		rtbstatus: string;
	}

	export interface SelectableFleetAsset extends FleetAsset {
		selected: boolean;
	}

	export interface Organization {
		organization_id: number;
		name: string;
		primary_support?: number;
		secondary_support?: number;
		webhook?: string;
		fleets?: Fleet[];
	}

	export interface AssetConfig {
		asset_type_id: number;
		asset_type_name: string;
		organization_id?: number;
		engine: string;
		gearbox: string;
		time_stamp: Date;
		author: string;
		asset_type_config?: string;
	}

	export interface ConfigTemplate {
		template_id: number;
		description: string;
		content?: string;
	}

	export interface ThresholdResult {
		xml: string;
		components: ComponentThreshold[];
	}
	export interface ComponentThreshold {
		key: string;
		description: string;
		thresholds: {
			scale: number[];
			offset: number[];
			mean: number[];
			warn: number[];
			alarm: number[];
		};
		stats: BoxPlotStats[];
		outliers: number[][];
	}

	export interface BoxPlotStats {
		low: number;
		q1: number;
		median: number;
		q3: number;
		high: number;
	}

	export interface FleetComparison {
		asset_id?: number;
		asset_name: string;
		stats: BoxPlotStats;
		outliers: number[];
	}

	export interface User {
		user_id: number;
		email: string;
		enabled: number;
		organization_id: number;
		organization_name?: string;
		// auth: string
		role: string;
		support: boolean;
		admin_notifications: number;
		// resetauth: string;
		// mfa_temp_secret: string;
		// mfa_secret: string;
		mfa_enabled?: boolean;
		sms_number: string;
		mute_imports: number;
		mute_commfails: number;
		hide_rtb?: number;
		hide_fdm?: number;
		hide_mech?: number;
	}

	export interface Device {
		device_id: number;
		serial: string;
		organization_id: number;
		// auth: string;
	}

	export interface OBCU {
		asset_id: number;
		asset_name: string;
		fleet_id: number;
		organization_id: number;
		asset_type_id: number;
		obcu_serial_number: string;
	}

	export interface Alert {
		alert_id: number;
		asset_id: number;
		component_key?: string;
		timestamp: Date;
		category: number;
		description: string;
		ack_by: number;
		ack_timestamp?: Date;
		ack_note?: string;
		ack_email?: string;
		component_name?: string;
	}

	export interface AssetComponentList {
		tach: AssemblyConfig.Tach[];
		shaft: AssemblyConfig.Shaft[];
		flutter: AssemblyConfig.Flutter[];
		engine: AssemblyConfig.Engine[];
		tracker: AssemblyConfig.Tracker[];
		sensor: AssemblyConfig.Sensor[];
		lru: AssemblyConfig.LRUDef[];
		alt_engine_name: string;
		alt_asset_icon: string;
		alt_asset_term: string;
		rul_capacity_factor: number;
		enable_counts_gui: boolean;
	}

	export interface Rotor {
		name: string;
		rotorid: string;
		balancedata: APIResults.BalanceData;
		rpm: string;
		blade: {
			name: string;
			id: string;
			displaygroup?: string;
		}[];
		adjdef: {
			name: string;
			id: string;
			max: string;
			min: string;
			step: string;
			displaygroup?: string;
		}[];
		vshaft: VShaft[];
		rotorhealth: string;
		ci: string;
		scale: string;
		offset: string;
		warn: string;
		alarm: string;
		var_s: string;
		var_r: string;
		mtbf: string;
		vibecoeffs: {
			shaftorder: {
				id: string;
				regime: {
					id: string;
					coeff: {
						_: string;
						vshaft: string;
					}[];
				}[];
			}[];
		};
		vibex: number[][];
		vibeSOByRegime: any;
		trackcoeff: {
			shaftorder: {
				id: string;
				regime: {
					id: string;
					coeff: {
						_: string;
						vshaft: string;
					}[];
				}[];
			}[];
		};
		trackx: number[][];
		trackSOByRegime: any;
		bladesets: {
			shaftorder: {
				id: string;
				set: {
					_: string;
					id: string;
				}[];
			}[];
		};
		PFA: string;
		vibeauto: {
			pref: {
				_: string;
				id: string;
			}[];
		};
		trkauto: {
			pref: {
				_: string;
				id: string;
			}[];
		};
		maxsplit?: number;
		inches: boolean;
	}

	export interface VibeCheck {
		name: string;
		rotorid: string;
		vshaft: APIResults.VibeCheckShaft[]
	}

	export interface VibeCheckShaft {
		id: string;
		name: string;
		shftid: string;
		shaft: {
			shftid: string;
			mult: number;
		};
		regime: number;
		ci: number;
		warn: number;
		alarm: number;
	}

	export interface VibeCheckResultRow {
		name: string;
		date?: Date;
		value?: number;
		warn: number;
		alarm: number;
	}

	export interface BalanceData {
		oneperrev: boolean;
		numadjustmenttypes: number;
		strikeroffset: number;
		actvibadj: number[][];
		acttrkadj: number[][];
		numblades: number;
		multdec: number;
		leastsquaresolve: boolean;
		oneperrevbladeset: number[][];
		bladeset: number[][];
		offset: number[][];
		pfa: number;
		clockwise: boolean;
		shaftbalance?: string;
		fixedpcr: number;
	}

	export interface VShaft {
		id: string;
		name: string;
		shftid: string;
		shaft: {
			shftid: string;
			mult: number;
		}[];
		ci: number[];
		scale: number[];
		offset: number[];
		warn: string;
		alarm: string;
		var_s: string;
		var_r: string;
		mtbf: string;
		default?: boolean;
		rotate?: number;
	}

	export interface Operation {
		operation_id: number;
		asset_id?: number;
		start_time: Date;
		end_time: Date;
		flight_time: number;
		rotor_turn_time: number;
		e1_turb: number;
		e1_comp: number;
		e1_imp: number;
		e2_turb: number;
		e2_comp: number;
		e2_imp: number;
		distance: number;
		exceedance_ack_by: number;
		exceedance_ack_time: Date;
		assembly_version: string;
		exceedance_count?: number;
	}

	export interface Acquisition {
		acquisition_id: number;
		asset_id: number;
		operation_id: number;
		component_key: string;
		device_id: number;
		regime_id: number;
		timestamp: Date;
		hi?: number;
		ci_1?: number
		ci_2?: number
		ci_3?: number
		ci_4?: number
		ci_5?: number
		ci_6?: number
		ci_7?: number
		ci_8?: number
		ci_9?: number
		ci_10?: number
		ci_11?: number
		ci_12?: number
		ci_13?: number
		ci_14?: number
		ci_15?: number
		ci_16?: number
		ci_17?: number
		ci_18?: number
		ci_19?: number
		ci_20?: number
		ci_21?: number
		ci_22?: number
		ci_23?: number
		ci_24?: number
	}

	export interface HiResult {
		hi: [any, number][];
		filtered?: [any, number][];
		prediction?: {
			predicted: [any, number][];
			upper: [any, number][];
			lower: [any, number][];
		};
		confidence: number;
		rul: number;
		dates?: number[];
		regimes?: number[];
	}

	export interface CiResult {
		ci: [any, number][];
		dates?: number[];
		regimes: [any, number][];
	}

	export interface VibeShaftResult {
		rpm: number;
		tsa: any[];
		fft: any[];
		dfrqtsa: number;
		spectrum: any[];
		dfrqspec: number;
		tsastats: DetailStatistics;
	}

	export interface VibeGearResult {
		res: any[];
		resstats: DetailStatistics;
		amres: any[];
		amresstats: DetailStatistics;
		eo: any[];
		eostats: DetailStatistics;
		am: any[];
		amstats: DetailStatistics;
		fm: any[];
		fmstats: DetailStatistics;
		rpm: number;
	}

	export interface VibeBearingResult {
		dfreq: number;
		spectrum: any[];
		markers: Marker[];
	}

	export interface VibeTachResult {
		tsa: any[];
		fft: any[];
		meanrpm: number;
		stdrpm: number;
		tsastats: DetailStatistics;
	}

	export interface SensorDetailResult {
		spectrum: any[];
		dfrqspec: number;
		rms: number;
		p2p: number;
	}

	export interface DetailStatistics {
		rms: number;
		kurtosis: number;
		crestfactor: number;
		peaktopeak: number;
	}

	export interface Marker {
		name: string;
		value: number;
	}

	export interface RTBCommon {
		data: {
			[component_id: string]: {
				regimes: {
					[regime_id: string]: {
						average: number[];
						prediction: number[];
						data: number[][];
					}
				}
			}
		};
	}

	export interface RTBResults extends RTBCommon {
		adjustments: number[][];
		alternates: number[][][];
		track: {
			[regime_id: string]: {
				track: number[];
				split: number;
				predtrack: number[];
				predsplit: number;
				leadlag?: number[];
			}
		};
	}

	export interface RTBH60Results extends RTBCommon {
		adjustments: string[][];
		alternates: string[][][];
		predictionAlternates?: number[][];
	}

	export interface RegimeIndex {
		[id: string]: {
			name: string;
			default: boolean;
		};
	}

	export interface RTBHistoryRegimeResult {
		regime: number;
		operations: RTBHistoryOperationResult[];
	}
	export interface RTBHistoryOperationResult {
		operation_id: number;
		ts: number;
		mag: number;
		phase: number;
	}
	export interface RTBHistoryResults {
		regimes: RTBHistoryRegimeResult[];
	}

	export interface TrackHistoryResult {
		regime: number;
		rows: [number, number][];
	}

	export interface AdjustmentHistory {
		adjustment_history_id: number;
		operation_start_time: Date;
		author_email: string;
		adjustments: string;
		user_note: string;
	}

	export interface MaintenanceHistory {
		maintenance_history_id: number;
		ts: Date;
		author_email: string;
		lru: number;
		user_note: string;
	}

	export interface VibeCheckAck {
		vibe_check_ack_id: number;
		ts: Date;
		author_email: string;
		user_note: string;
	}

	export interface FirmwareStatus {
		operation_id: number;
		dev_id: number;
		dev_type: string;
		dev_lbl: string;
		dev_comms: number;
		hw_tgt: number;
		hw_mdate: Date;
		hw_tunei: number;
		hw_tunef: number;
		appver: string;
		bootver: string;
		target: string;
	}

	export interface TrackResults {
		track: number[][];
		leadlag: {
			[regime_id: string]: number[];
		};
	}

	export interface Exceedance {
		timestamp: Date;
		exceedance_type: number;
		name?: string;
		description?: string;
		severity?: number;
		duration?: number;
	}

	export interface OpExceedances {
		rows: Exceedance[];
		ackUser: string;
		ackTime: Date;
	}

	export interface Breadcrumb {
		timestamp: Date;
		lat: number;
		lng: number;
		alt: number;
		spd: number;
		hdg: number;
		reg: number;
	}

	export interface PathRow {
		ts: Date;
		lat: number;
		lng: number;
	}

	export interface FleetMapPoint {
		index: number;
		asset_id: number;
		asset_name: string;
		ts: Date;
		lat: number;
		lng: number;
		alt: number;
		spd: number;
		hdg: number;
		reg: number;
	}

	export interface RawPairsResult {
		ts: Date;
		tachrawid: number;
		viberawid: number;
	}

	export interface RawSinglesResult {
		ts: Date;
		rawid: number;
	}

	export interface RegimeCountsResult {
		vibe: RegimeCount[];
		track?: RegimeCount[];
	}

	export interface RegimeCount {
		regime_id: number;
		count: number;
		evaluate?: boolean;
		name?: string;
	}

	export interface SystemLog {
		id: number;
		ts: Date;
		ec2_instance: string;
		environment_name: string;
		user_id: number;
		remote_ip: string;
		status_code: number;
		method: string;
		path: string;
		query: string;
		response_time_us: number;
		db_time_us: number;
		analysis_time_us: number;
		request_size: number;
		response_size: number;
		message: string;
		exception: string;
		type: number;
		user_email: string;
		user_role: string;
	}

	export interface ItemPage {
		rows: any[];
		count: number;
	}

	export interface SystemLogPage extends ItemPage {
		rows: SystemLog[];
	}

	export interface AlertPage extends ItemPage {
		rows: Alert[];
	}

	export interface CommFailurePage extends ItemPage {
		rows: CommFailure[];
	}

	export interface AssetAccessPage extends ItemPage {
		rows: AssetAccess[];
	}

	export interface SentEmailPage extends ItemPage {
		rows: SentEmail[];
	}

	export interface DBComponentState extends AssemblyConfig.ComponentState {
		asset_id: number;
		component_key: string;
		is_rtb: number;
		rul_confidence: number;
	}

	export interface StaleAsset {
		asset_id: number;
		asset_type_id: number;
		asset_name: string;
		fleet_id: number;
		last_update: Date;
		last_acquisition: Date;
	}

	export interface StateResult {
		ts: number[];
		regime_id: number[];
		params: {
			name: string;
			units: string;
			values: number[];
		}[];
	}

	export interface TaskState {
		status: string;
		progress: number;
		complete: boolean;
		duration: number;
	}

	export interface NotificationFleet {
		fleet_id: number;
		fleet_name: string;
		organization_id?: number;
		organization_name?: string;
		enable_email: boolean;
		enable_sms: boolean;
	}

	export interface NotificationAsset {
		asset_id: number;
		asset_name: string;
		asset_serial_number: string;
		fleet_name: string;
		organization_name: string;
		enable_email: boolean;
		enable_sms: boolean;
	}

	export interface FlightPathFrame {
		ts: number;
		regime_id: number;
		latitude: number;
		longitude: number;
		altitude: number;
		heading: number;
		pitch: number;
		roll: number;
	}

	export interface FileListing {
		directories: string[];
		files: string[];
	}

	export interface DataFile {
		datafile_id: number;
		operation_id: number;
		filename: string;
		filetype: string | null;
		imported: Date;
		size: number;
		status: string;
	}

	export interface CommFailure {
		operation_id: number;
		operation_start_time: Date;
		ts: Date;
		device: number;
		device_name: string;
		source: string;
		status: string;
	}

	export interface AssetAccess {
		user_id: number;
		email: string;
		ts: Date;
	}

	export interface SentEmail {
		message_id: number;
		timestamp: Date;
		recipient: string;
		subject: string;
		status: string;
		asset_id: number;
		fleet_id?: number;
		asset_name?: string;
	}

	export interface KnowledgeBase {
		title: string;
		url: string;
	}

	export interface UserLoginCounts {
		email: string;
		day: number;
		month: number;
		semi: number;
	}

	export interface FirmwareResult {
		devices: FirmwareStatus[];
		ts?: Date;
	}

	export interface AssetFirmwareInfo {
		commandFile: boolean;
		currentTarget: number;
		currentTargetName: string;
		firmware: FirmwareTargetInfo[];
	}

	export interface FirmwareTargetInfo {
		name: string;
		controlTarget: number;
		regimeTarget: number;
		stateTarget: number;
		path: string;
		control?: string;
		regime?: string;
		state?: string;
		webpage?: string;
	}
}

export interface AssetRoutable {
	asset_id: number;
	fleet_id: number;
}